import { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import moment from 'moment-timezone'
import { Edit2, X, Save, Repeat } from 'lucide-react'

const ET = 'America/New_York'

const BookingManagement = () => {
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [showPastBookings, setShowPastBookings] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [actionLoading, setActionLoading] = useState(null)
  const [editingBooking, setEditingBooking] = useState(null)
  const [editFormData, setEditFormData] = useState(null)
  const [groupActionLoading, setGroupActionLoading] = useState(null)

  // Fetch bookings
  const fetchBookings = async () => {
    try {
      setLoading(true)
      setError('')
      const params = statusFilter !== 'all' ? { status: statusFilter } : {}
      const response = await axios.get('/api/bookings', { params })
      setBookings(response.data)
    } catch (err) {
      setError('Failed to load bookings. Please try again.')
      console.error('Error fetching bookings:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchBookings()
  }, [statusFilter])

  // Handle approve booking
  const handleApprove = async (bookingId) => {
    try {
      setActionLoading(bookingId)
      await axios.post(`/api/bookings/${bookingId}/approve`)
      await fetchBookings()
      setSelectedBooking(null)
    } catch (err) {
      alert('Failed to approve booking: ' + (err.response?.data?.message || err.message))
    } finally {
      setActionLoading(null)
    }
  }

  // Handle reject booking
  const handleReject = async (bookingId) => {
    const reason = prompt('Please provide a reason for rejection (optional):')
    if (reason === null) return // User cancelled

    try {
      setActionLoading(bookingId)
      await axios.post(`/api/bookings/${bookingId}/reject`, { rejectionReason: reason })
      await fetchBookings()
      setSelectedBooking(null)
    } catch (err) {
      alert('Failed to reject booking: ' + (err.response?.data?.message || err.message))
    } finally {
      setActionLoading(null)
    }
  }

  // Handle cancel booking (member requested)
  const handleCancel = async (bookingId) => {
    const reason = prompt('Add a note for the cancellation (optional):')
    if (reason === null) return

    try {
      setActionLoading(bookingId)
      await axios.post(`/api/bookings/${bookingId}/cancel`, { cancellationReason: reason })
      await fetchBookings()
      setSelectedBooking(null)
    } catch (err) {
      alert('Failed to cancel booking: ' + (err.response?.data?.message || err.message))
    } finally {
      setActionLoading(null)
    }
  }

  // Handle edit booking
  const handleEditClick = (booking) => {
    setEditingBooking(booking)
    setEditFormData({
      facility: booking.facility,
      startDate: moment.tz(booking.startDate, ET).format('YYYY-MM-DD'),
      startTime: booking.startTime,
      endTime: booking.endTime,
      contactName: booking.contactName,
      contactEmail: booking.contactEmail,
      contactPhone: booking.contactPhone || '',
      eventType: booking.eventType,
      numberOfGuests: booking.numberOfGuests || 1,
      eventDescription: booking.eventDescription || '',
      notes: booking.notes || ''
    })
  }

  // Handle edit form changes
  const handleEditChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Handle save edited booking
  const handleSaveEdit = async () => {
    try {
      setActionLoading(editingBooking._id)
      await axios.put(`/api/bookings/${editingBooking._id}`, {
        ...editFormData,
        startDate: moment.tz(editFormData.startDate, 'YYYY-MM-DD', ET).toISOString(),
        endDate: moment.tz(editFormData.startDate, 'YYYY-MM-DD', ET).toISOString()
      })
      await fetchBookings()
      setEditingBooking(null)
      setEditFormData(null)
      alert('Booking updated successfully!')
    } catch (err) {
      alert('Failed to update booking: ' + (err.response?.data?.message || err.message))
    } finally {
      setActionLoading(null)
    }
  }

  // Handle approve all in recurrence group
  const handleApproveGroup = async (groupId) => {
    try {
      setGroupActionLoading(groupId)
      await axios.post(`/api/bookings/recurrence-group/${groupId}/approve`)
      await fetchBookings()
      setSelectedBooking(null)
    } catch (err) {
      alert('Failed to approve series: ' + (err.response?.data?.message || err.message))
    } finally {
      setGroupActionLoading(null)
    }
  }

  // Handle reject all in recurrence group
  const handleRejectGroup = async (groupId) => {
    const reason = prompt('Please provide a reason for rejecting the entire series (optional):')
    if (reason === null) return

    try {
      setGroupActionLoading(groupId)
      await axios.post(`/api/bookings/recurrence-group/${groupId}/reject`, { rejectionReason: reason })
      await fetchBookings()
      setSelectedBooking(null)
    } catch (err) {
      alert('Failed to reject series: ' + (err.response?.data?.message || err.message))
    } finally {
      setGroupActionLoading(null)
    }
  }

  // Build recurrence group stats: { groupId: { total, pending, approved, rejected } }
  const recurrenceGroups = useMemo(() => {
    const groups = {}
    for (const booking of bookings) {
      if (!booking.recurrenceGroupId) continue
      const gid = booking.recurrenceGroupId
      if (!groups[gid]) groups[gid] = { total: 0, pending: 0, approved: 0, rejected: 0 }
      groups[gid].total++
      if (booking.status === 'pending') groups[gid].pending++
      else if (booking.status === 'approved') groups[gid].approved++
      else if (booking.status === 'rejected') groups[gid].rejected++
    }
    return groups
  }, [bookings])

  // Format date in Eastern Time
  const formatDate = (dateString) => {
    return moment.tz(dateString, ET).format('MMM D, YYYY')
  }

  // Format time to 12-hour format
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A'
    // timeString is in format "HH:mm" (24-hour)
    return moment(timeString, 'HH:mm').format('h:mm A')
  }

  // Format facility name
  const formatFacility = (facility) => {
    const facilityMap = {
      'pavilion': 'Pavilion',
      'taj': 'Taj Mahal',
      'taj_mahal': 'Taj Mahal',
      'court_1_lower_deck': 'Court 1 - Lower Deck',
      'court_2_lower_creek': 'Court 2 - Lower Creek',
      'court_3_upper_pavilion': 'Court 3 - Upper Pavilion',
      'court_4_upper_creek': 'Court 4 - Upper Creek'
    }
    return facilityMap[facility] || facility
  }

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-orange-100 text-orange-800',
    }
    return badges[status] || 'bg-gray-100 text-gray-800'
  }

  // Filter bookings by date (past vs upcoming)
  const filteredBookings = showPastBookings
    ? bookings
    : bookings.filter(booking => {
        const bookingDate = moment.tz(booking.startDate, ET)
        const today = moment().startOf('day')
        return bookingDate.isSameOrAfter(today)
      })

  // Count upcoming bookings
  const upcomingCount = bookings.filter(booking => {
    const bookingDate = moment.tz(booking.startDate, ET)
    const today = moment().startOf('day')
    return bookingDate.isSameOrAfter(today)
  }).length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Booking Management</h1>
          <p className="text-gray-600 mt-1">Review and manage facility and tennis court reservation requests</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <div className="space-y-4">
          {/* Date Filter Toggle */}
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-gray-700">
              Show:
            </label>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
              <button
                onClick={() => setShowPastBookings(false)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  !showPastBookings
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                Upcoming ({upcomingCount})
              </button>
              <button
                onClick={() => setShowPastBookings(true)}
                className={`px-4 py-2 text-sm font-medium transition-colors border-l border-gray-300 ${
                  showPastBookings
                    ? 'bg-primary-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                All Bookings ({bookings.length})
              </button>
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex-1"></div>
            <button
              onClick={fetchBookings}
              className="px-4 py-2 text-primary-600 hover:text-primary-700 font-medium flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Bookings Table */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
            <p className="mt-1 text-sm text-gray-500">
              No bookings match the selected filter.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facility
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBookings.map((booking) => (
                  <tr key={booking._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-semibold text-sm">
                            {booking.contactName?.charAt(0) || 'M'}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {booking.contactName || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">{booking.contactEmail || 'N/A'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatFacility(booking.facility) || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {booking.startDate ? formatDate(booking.startDate) : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {booking.startTime && booking.endTime
                          ? `${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}`
                          : 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                            booking.status
                          )}`}
                        >
                          {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || 'N/A'}
                        </span>
                        {booking.recurrenceGroupId && recurrenceGroups[booking.recurrenceGroupId] && (
                          <span className="px-2 inline-flex items-center gap-1 text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            <Repeat className="w-3 h-3" />
                            Series ({recurrenceGroups[booking.recurrenceGroupId].total})
                          </span>
                        )}
                        {moment.tz(booking.startDate, ET).isBefore(moment().startOf('day')) && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-300 text-gray-700">
                            Past
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex flex-col items-end gap-1">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setSelectedBooking(booking)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View
                          </button>
                          {booking.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(booking._id)}
                                disabled={actionLoading === booking._id}
                                className="text-tertiary-600 hover:text-tertiary-900 disabled:opacity-50"
                              >
                                {actionLoading === booking._id ? 'Processing...' : 'Approve'}
                              </button>
                              <button
                                onClick={() => handleReject(booking._id)}
                                disabled={actionLoading === booking._id}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              >
                                Reject
                              </button>
                              <button
                                onClick={() => handleCancel(booking._id)}
                                disabled={actionLoading === booking._id}
                                className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {booking.status === 'approved' && (
                            <>
                              <button
                                onClick={() => handleEditClick(booking)}
                                disabled={actionLoading === booking._id}
                                className="text-primary-600 hover:text-primary-900 disabled:opacity-50 flex items-center gap-1"
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleReject(booking._id)}
                                disabled={actionLoading === booking._id}
                                className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              >
                                Reject
                              </button>
                              <button
                                onClick={() => handleCancel(booking._id)}
                                disabled={actionLoading === booking._id}
                                className="text-orange-600 hover:text-orange-900 disabled:opacity-50"
                              >
                                Cancel
                              </button>
                            </>
                          )}
                          {booking.status === 'rejected' && (
                            <button
                              onClick={() => handleApprove(booking._id)}
                              disabled={actionLoading === booking._id}
                              className="text-tertiary-600 hover:text-tertiary-900 disabled:opacity-50"
                            >
                              {actionLoading === booking._id ? 'Processing...' : 'Approve'}
                            </button>
                          )}
                        </div>
                        {booking.recurrenceGroupId && recurrenceGroups[booking.recurrenceGroupId]?.pending > 0 && booking.status === 'pending' && (
                          <div className="flex gap-2 mt-1">
                            <button
                              onClick={() => handleApproveGroup(booking.recurrenceGroupId)}
                              disabled={groupActionLoading === booking.recurrenceGroupId}
                              className="text-xs text-purple-700 hover:text-purple-900 font-medium disabled:opacity-50"
                            >
                              {groupActionLoading === booking.recurrenceGroupId ? 'Processing...' : `Approve All (${recurrenceGroups[booking.recurrenceGroupId].pending})`}
                            </button>
                            <button
                              onClick={() => handleRejectGroup(booking.recurrenceGroupId)}
                              disabled={groupActionLoading === booking.recurrenceGroupId}
                              className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                            >
                              Reject All
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Booking Details</h3>
              <button
                onClick={() => setSelectedBooking(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="px-6 py-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Contact Name</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedBooking.contactName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="mt-1 text-sm text-gray-900 break-all">{selectedBooking.contactEmail || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedBooking.contactPhone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Facility</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatFacility(selectedBooking.facility) || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Start Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedBooking.startDate ? formatDate(selectedBooking.startDate) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">End Date</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {selectedBooking.endDate ? formatDate(selectedBooking.endDate) : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Start Time</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatTime(selectedBooking.startTime)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">End Time</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatTime(selectedBooking.endTime)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className="mt-1">
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(
                        selectedBooking.status
                      )}`}
                    >
                      {selectedBooking.status?.charAt(0).toUpperCase() +
                        selectedBooking.status?.slice(1) || 'N/A'}
                    </span>
                  </p>
                </div>
                {selectedBooking.eventType && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Event Type</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBooking.eventType}</p>
                  </div>
                )}
                {selectedBooking.numberOfGuests && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Number of Guests</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedBooking.numberOfGuests}</p>
                  </div>
                )}
              </div>
              {selectedBooking.recurrenceGroupId && recurrenceGroups[selectedBooking.recurrenceGroupId] && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Repeat className="w-4 h-4 text-purple-600" />
                    <label className="text-sm font-medium text-purple-800">Recurring Series</label>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-purple-600 font-medium">Pattern:</span>
                      <span className="ml-1 text-purple-900 capitalize">{selectedBooking.recurrencePattern || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-purple-600 font-medium">Total:</span>
                      <span className="ml-1 text-purple-900">{recurrenceGroups[selectedBooking.recurrenceGroupId].total}</span>
                    </div>
                    <div>
                      <span className="text-purple-600 font-medium">Pending:</span>
                      <span className="ml-1 text-purple-900">{recurrenceGroups[selectedBooking.recurrenceGroupId].pending}</span>
                    </div>
                    <div>
                      <span className="text-purple-600 font-medium">Approved:</span>
                      <span className="ml-1 text-purple-900">{recurrenceGroups[selectedBooking.recurrenceGroupId].approved}</span>
                    </div>
                  </div>
                </div>
              )}
              {selectedBooking.eventDescription && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Event Description</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {selectedBooking.eventDescription}
                  </p>
                </div>
              )}
              {selectedBooking.specialRequests && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Special Requests</label>
                  <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                    {selectedBooking.specialRequests}
                  </p>
                </div>
              )}
              {selectedBooking.rejectionReason && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Rejection Reason</label>
                  <p className="mt-1 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
                    {selectedBooking.rejectionReason}
                  </p>
                </div>
              )}
            </div>
            {selectedBooking.status === 'pending' && (
              <div className="bg-gray-50 px-6 py-4 space-y-3">
                {selectedBooking.recurrenceGroupId && recurrenceGroups[selectedBooking.recurrenceGroupId]?.pending > 1 && (
                  <div className="flex flex-col sm:flex-row justify-end gap-3 pb-3 border-b border-gray-200">
                    <button
                      onClick={() => handleRejectGroup(selectedBooking.recurrenceGroupId)}
                      disabled={groupActionLoading === selectedBooking.recurrenceGroupId}
                      className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Repeat className="w-4 h-4" />
                      Reject All in Series ({recurrenceGroups[selectedBooking.recurrenceGroupId].pending})
                    </button>
                    <button
                      onClick={() => handleApproveGroup(selectedBooking.recurrenceGroupId)}
                      disabled={groupActionLoading === selectedBooking.recurrenceGroupId}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Repeat className="w-4 h-4" />
                      {groupActionLoading === selectedBooking.recurrenceGroupId ? 'Processing...' : `Approve All in Series (${recurrenceGroups[selectedBooking.recurrenceGroupId].pending})`}
                    </button>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row justify-end gap-3">
                  <button
                    onClick={() => handleCancel(selectedBooking._id)}
                    disabled={actionLoading === selectedBooking._id}
                    className="px-4 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 font-medium disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleReject(selectedBooking._id)}
                    disabled={actionLoading === selectedBooking._id}
                    className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 font-medium disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedBooking._id)}
                    disabled={actionLoading === selectedBooking._id}
                    className="px-4 py-2 bg-tertiary-600 text-white rounded-lg hover:bg-tertiary-700 font-medium disabled:opacity-50"
                  >
                    {actionLoading === selectedBooking._id ? 'Processing...' : 'Approve'}
                  </button>
                </div>
              </div>
            )}
            {selectedBooking.status === 'approved' && (
              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => handleCancel(selectedBooking._id)}
                  disabled={actionLoading === selectedBooking._id}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium disabled:opacity-50"
                >
                  {actionLoading === selectedBooking._id ? 'Processing...' : 'Cancel Booking'}
                </button>
                <button
                  onClick={() => handleReject(selectedBooking._id)}
                  disabled={actionLoading === selectedBooking._id}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {actionLoading === selectedBooking._id ? 'Processing...' : 'Reject Booking'}
                </button>
              </div>
            )}
            {selectedBooking.status === 'rejected' && (
              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={() => setSelectedBooking(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Close
                </button>
                <button
                  onClick={() => handleApprove(selectedBooking._id)}
                  disabled={actionLoading === selectedBooking._id}
                  className="px-4 py-2 bg-tertiary-600 text-white rounded-lg hover:bg-tertiary-700 font-medium disabled:opacity-50"
                >
                  {actionLoading === selectedBooking._id ? 'Processing...' : 'Approve Booking'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Booking Modal */}
      {editingBooking && editFormData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full my-8">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Edit Booking
              </h2>
              <button
                onClick={() => {
                  setEditingBooking(null)
                  setEditFormData(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              {/* Facility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Facility <span className="text-red-500">*</span>
                </label>
                <select
                  value={editFormData.facility}
                  onChange={(e) => handleEditChange('facility', e.target.value)}
                  className="input"
                >
                  <option value="pavilion">Pavilion</option>
                  <option value="taj_mahal">Taj Mahal</option>
                  <option value="court_1_lower_deck">Court 1 - Lower Deck Side</option>
                  <option value="court_2_lower_creek">Court 2 - Lower Creek Side</option>
                  <option value="court_3_upper_pavilion">Court 3 - Upper Pavilion Side</option>
                  <option value="court_4_upper_creek">Court 4 - Upper Creek Side</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={editFormData.startDate}
                  onChange={(e) => handleEditChange('startDate', e.target.value)}
                  className="input"
                />
              </div>

              {/* Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={editFormData.startTime}
                    onChange={(e) => handleEditChange('startTime', e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={editFormData.endTime}
                    onChange={(e) => handleEditChange('endTime', e.target.value)}
                    className="input"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.contactName}
                    onChange={(e) => handleEditChange('contactName', e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={editFormData.contactEmail}
                    onChange={(e) => handleEditChange('contactEmail', e.target.value)}
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={editFormData.contactPhone}
                  onChange={(e) => handleEditChange('contactPhone', e.target.value)}
                  className="input"
                />
              </div>

              {/* Event Type and Guests */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.eventType}
                    onChange={(e) => handleEditChange('eventType', e.target.value)}
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Guests
                  </label>
                  <input
                    type="number"
                    value={editFormData.numberOfGuests}
                    onChange={(e) => handleEditChange('numberOfGuests', parseInt(e.target.value))}
                    className="input"
                    min="1"
                  />
                </div>
              </div>

              {/* Event Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Description
                </label>
                <textarea
                  value={editFormData.eventDescription}
                  onChange={(e) => handleEditChange('eventDescription', e.target.value)}
                  rows={3}
                  className="input"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => handleEditChange('notes', e.target.value)}
                  rows={2}
                  className="input"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setEditingBooking(null)
                  setEditFormData(null)
                }}
                className="btn btn-secondary"
                disabled={actionLoading === editingBooking._id}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={actionLoading === editingBooking._id}
                className="btn btn-primary flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                {actionLoading === editingBooking._id ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BookingManagement
